import React, { useEffect } from 'react';
import {
    MapContainer,
    TileLayer,
    Marker,
    Polyline,
    Tooltip,
    Popup,
    useMap,
} from 'react-leaflet';
import L from 'leaflet';
import originIconUrl from 'assets/img/illustrations/locationOrigin.svg'
import destIconUrl from 'assets/img/illustrations/locationDest.svg';
import shipIconUrl from 'assets/img/illustrations/shipDark.svg';

// Ícones personalizados
const originIcon = new L.Icon({
    iconUrl: originIconUrl,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
});

const destinationIcon = new L.Icon({
    iconUrl: destIconUrl,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
});

const shipIcon = new L.Icon({
    iconUrl: shipIconUrl,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
});

// Componente para centralizar mapa ao carregar
const AutoCenterMap = ({ positions }) => {
    const map = useMap();

    useEffect(() => {
        if (positions.length > 0) {
            const bounds = L.latLngBounds(positions);
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [positions, map]);

    return null;
};

// Botão de centralização manual
const CenterMapButton = ({ positions }) => {
    const map = useMap();

    const handleClick = () => {
        const bounds = L.latLngBounds(positions);
        map.fitBounds(bounds, { padding: [50, 50] });
    };

    return (
        <button
            onClick={handleClick}
            style={{
                position: 'absolute',
                top: 20,
                right: 20,
                zIndex: 1000,
                background: '#fff',
                color: '#000',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                cursor: 'pointer',
                fontWeight: 'bold',
            }}
        >
            Centralizar
        </button>
    );
};

// Legenda no canto inferior esquerdo
const MapLegend = () => {
    const map = useMap();

    useEffect(() => {
        const legend = L.control({ position: 'bottomleft' });

        legend.onAdd = function () {
            const div = L.DomUtil.create('div', 'map-legend');
            div.innerHTML = `
        <div style="
          background: white;
          padding: 6px 10px;
          border: 1px solid #ccc;
          border-radius: 6px;
          font-size: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        ">
          <div style="margin-bottom: 4px;">
            <span style="display: inline-block; width: 20px; height: 2px; background: #3B82F6; border-top: 2px dashed #3B82F6; margin-right: 6px;"></span>
            Rota planejada
          </div>
          <div>
            <span style="display: inline-block; width: 20px; height: 2px; background: #DC2626; margin-right: 6px;"></span>
            Rota atual
          </div>
          <div>
            <span style="display: inline-block; width: 20px; height: 2px; background: #000; margin-right: 6px;"></span>
            Posição atual
          </div>
        </div>
      `;
            return div;
        };

        legend.addTo(map);

        return () => {
            legend.remove();
        };
    }, [map]);

    return null;
};

// Componente principal do mapa
const TrackingMap = ({ dataTrackingWorld }) => {
    if (typeof dataTrackingWorld === 'string') {
        try {
            dataTrackingWorld = JSON.parse(dataTrackingWorld);
        } catch (err) {
            return <p>Erro ao carregar dados do mapa</p>;
        }
    }


    const allPositions = [];

    const markers = dataTrackingWorld.flatMap((track, idx) => {
        const { coordinates = {} } = track;
        const locations = coordinates.locations || [];
        if (locations.length === 0) return [];

        const origin = locations[0];
        const destination = locations[locations.length - 1];
        const originPos = origin?.position;
        const destPos = destination?.position;

        const features = [];

        if (originPos?.length === 2) {
            const pos = [originPos[0], originPos[1]];
            allPositions.push(pos);
            features.push(
                <Marker key={`origin-${idx}`} position={pos} icon={originIcon}>
                    <Tooltip direction="top" offset={[0, -10]} permanent>
                        {origin.description || 'Origem'}
                    </Tooltip>
                </Marker>
            );
        }

        if (destPos?.length === 2) {
            const pos = [destPos[0], destPos[1]];
            allPositions.push(pos);
            features.push(
                <Marker key={`dest-${idx}`} position={pos} icon={destinationIcon}>
                    <Tooltip direction="top" offset={[0, -10]} permanent>
                        {destination.description || 'Destino'}
                    </Tooltip>
                </Marker>
            );
        }

        return features;
    });

    const polylines = dataTrackingWorld.map((track, idx) => {
        const planned = track.coordinates?.plannedPath || [];
        const actual = track.coordinates?.actualPath?.[0]?.path || [];

        const plannedLatLngs = planned.map(([lat, lon]) => [lat, lon]);
        const actualLatLngs = actual.map(([lat, lon]) => [lat, lon]);

        plannedLatLngs.forEach(p => allPositions.push(p));
        actualLatLngs.forEach(p => allPositions.push(p));

        const lastActualPos = actualLatLngs[actualLatLngs.length - 1];

        return (
            <React.Fragment key={`paths-${idx}`}>
                {plannedLatLngs.length >= 2 && (
                    <Polyline
                        positions={plannedLatLngs}
                        pathOptions={{
                            color: '#3B82F6',
                            weight: 3,
                            opacity: 0.7,
                            dashArray: '8',
                        }}
                    />
                )}
                {actualLatLngs.length >= 2 && (
                    <Polyline
                        positions={actualLatLngs}
                        pathOptions={{
                            color: '#DC2626',
                            weight: 4,
                            opacity: 0.9,
                        }}
                    />
                )}
                {lastActualPos && (
                    <Marker position={lastActualPos} icon={shipIcon}>
                        <Popup>Posição atual do navio</Popup>
                    </Marker>
                )}
            </React.Fragment>
        );
    });

    const center = allPositions.length > 0
        ? [
            allPositions.reduce((sum, p) => sum + p[0], 0) / allPositions.length,
            allPositions.reduce((sum, p) => sum + p[1], 0) / allPositions.length,
        ]
        : [0, 0];

    return (
        <>
            <MapContainer
                center={center}
                zoom={3}
                scrollWheelZoom={true}
                style={{ height: '400px', width: '100%', borderRadius: '10px' }}
            >
                <TileLayer
                    attribution='&copy; Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
                />
                <MapLegend />
                {markers}
                {polylines}
                <AutoCenterMap positions={allPositions} />
                <CenterMapButton positions={allPositions} />

            </MapContainer>
        </>
    );
};

export default TrackingMap;
